// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	type HolidaySidePeekPack,
	registerHolidaySidePeekPack,
} from '@app/features/holiday/components/HolidaySidePeekResolvers';
import SpookyLeft from '@app/features/holiday/packs/halloween/assets/peeking/side/spooky-left.svg?react';
import SpookyMiddle from '@app/features/holiday/packs/halloween/assets/peeking/side/spooky-middle.svg?react';
import SpookyRight from '@app/features/holiday/packs/halloween/assets/peeking/side/spooky-right.svg?react';
import {isHolidaySidePeeksEnabled} from '@app/features/holiday/state/HolidayPreferences';
import {enabledHolidaySeasonPhase} from '@app/features/holiday/utils/holidaySeason';

/** DEV-only. `true` uses Halloween side peekers on delete outside peak. Production builds never force. */
export const DEBUG_FORCE_HOLIDAY_SIDE_PEEK: true | null = null;

export const HALLOWEEN_SIDE_PEEK_HEIGHT_REM = 28;
export const HALLOWEEN_SIDE_PEEK_MAX_WIDTH_PERCENT = 40;
export const HALLOWEEN_SIDE_PEEK_MAX_HEIGHT_PERCENT = 60;
/** Skeleton gripping fingers sit further into the column than the witch/ghost. */
export const HALLOWEEN_SKELETON_OFFSET_X_PERCENT = 20;

export function isHalloweenSidePeekForced(): boolean {
	return import.meta.env.DEV && DEBUG_FORCE_HOLIDAY_SIDE_PEEK === true;
}

export function isHalloweenSidePeekEnabled(): boolean {
	if (isHalloweenSidePeekForced()) return true;
	return enabledHolidaySeasonPhase('halloween') === 'peak' && isHolidaySidePeeksEnabled('halloween');
}

export const HALLOWEEN_SIDE_PEEK_PACK: HolidaySidePeekPack = {
	id: 'halloween',
	seasonal: true,
	heightRem: HALLOWEEN_SIDE_PEEK_HEIGHT_REM,
	maxWidthPercent: HALLOWEEN_SIDE_PEEK_MAX_WIDTH_PERCENT,
	maxHeightPercent: HALLOWEEN_SIDE_PEEK_MAX_HEIGHT_PERCENT,
	chance: () => 1,
	isEnabled: isHalloweenSidePeekEnabled,
	peekers: [
		{id: 'spooky-left', Svg: SpookyLeft, overlayMemberList: true},
		{id: 'spooky-middle', Svg: SpookyMiddle, overlayMemberList: true},
		{
			id: 'spooky-right',
			Svg: SpookyRight,
			offsetXPercent: HALLOWEEN_SKELETON_OFFSET_X_PERCENT,
			overlayMemberList: true,
		},
	],
};

let registered = false;
let unsubscribe: (() => void) | null = null;
let debugPeekerIndex = 0;

function dispose(): void {
	unsubscribe?.();
	unsubscribe = null;
	registered = false;
}

export function nextHalloweenSidePeekerId(): string {
	const peekers = HALLOWEEN_SIDE_PEEK_PACK.peekers;
	const peeker = peekers[debugPeekerIndex % peekers.length] ?? peekers[0];
	debugPeekerIndex += 1;
	return peeker.id;
}

export function registerHalloweenSidePeekers(): void {
	if (registered) return;
	registered = true;
	unsubscribe = registerHolidaySidePeekPack(HALLOWEEN_SIDE_PEEK_PACK);
}

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		dispose();
	});
}
